import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import theme from '../../styles/themes/default';
import useRealTimeScoreStyles from '../../styles/kozelBattle/realTimeScore';
import { keyframes } from '@mui/system';
import realTimeScoreImg from '../../assets/images/kozel_battle/real_time_score.png';
import lightVsDark from '../../assets/images/kozel_battle/light_vs_dark2.png';
import likesDark from '../../assets/images/kozel_battle/likes_dark.png';
import likesWhite from '../../assets/images/kozel_battle/likes_white.png';
import { useAppDispatch, useAppSelector } from '../../stores/store';
import { fetchKozelTotals } from '../../features/games/kozelBattleSlice';
import promoAlcool from '../../assets/images/kozel_battle/promo-despre-alcool.png';

// Define the flip animation keyframes (same as Counter.tsx)
const flipAnimation = keyframes`
  0% {
    transform: rotateX(0);
  }
  50% {
    transform: rotateX(-90deg);
  }
  100% {
    transform: rotateX(0);
  }
`;

interface ScoreDisplayProps {
  score: number;
  previousScore: string;
  animatingDigits: number[];
}

const ScoreDisplay: React.FC<ScoreDisplayProps> = ({ score, previousScore, animatingDigits }) => {
  const formattedScore = score.toString().padStart(4, '0');
  const scoreDigits = formattedScore.split('');

  return (
    <Box sx={{ display: 'flex' }}>
      {scoreDigits.map((digit, index) => (
        <Box
          key={index}
          sx={{
            position: 'relative',
            width: { xs: 25, sm: 45 },
            height: { xs: 35, sm: 65 },
            margin: { xs: '0 1px', sm: '0 3px' },
            borderRadius: '8px',
            overflow: 'visible',
          }}
        >
          {/* Panoul principal cu cifra */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: theme.colors.espresso_brown,
              color: 'white',
              fontSize: { xs: '2rem', sm: '3rem' },
              fontWeight: 'bold',
              borderRadius: '8px',
              boxShadow: '0px 5px 10px rgba(0, 0, 0, 0.4)',
              fontFamily: theme.fonts.apex,
              paddingTop: { xs: '5px', sm: '8px' },
              animation: animatingDigits.includes(index)
                ? `${flipAnimation} 0.7s ease-in-out`
                : 'none',
              transformStyle: 'preserve-3d',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '50%',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                zIndex: 3,
              }
            }}
          >
            {digit}

            {/* Linia pe mijloc */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: 0,
                width: '100%',
                height: '2px',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                zIndex: 10,
              }}
            />
          </Box>

          {/* Elementele laterale stânga */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: '85%',
              transform: 'translateY(-50%)',
              width: { xs: 3, sm: 5 },
              height: '70%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              zIndex: 5,
            }}
          >
            <Box sx={{
              width: '100%',
              height: 10,
              backgroundColor: theme.colors.espresso_brown,
              borderRadius: '2px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
              border: '1px solid #2a1f12'
            }} />
          </Box>

          {/* Elementele laterale dreapta */}
          <Box
            sx={{
              position: 'absolute',
              right: 1,
              top: '85%',
              transform: 'translateY(-50%)',
              width: { xs: 3, sm: 5 },
              height: '70%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              zIndex: 5,
            }}
          >
            <Box sx={{
              width: '100%',
              height: 10,
              backgroundColor: theme.colors.espresso_brown,
              borderRadius: '2px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
              border: '1px solid #2a1f12'
            }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const RealTimeScore: React.FC = () => {
  const classes = useRealTimeScoreStyles();
  const dispatch = useAppDispatch();
  const { totalLightScore, totalDarkScore } = useAppSelector((state) => state.kozelBattle);

  const [previousLightScore, setPreviousLightScore] = useState('0000');
  const [previousDarkScore, setPreviousDarkScore] = useState('0000');
  const [animatingLightDigits, setAnimatingLightDigits] = useState<number[]>([]);
  const [animatingDarkDigits, setAnimatingDarkDigits] = useState<number[]>([]);

  // Fetch totals on mount and every 30 seconds
  useEffect(() => {
    dispatch(fetchKozelTotals());
    const interval = setInterval(() => dispatch(fetchKozelTotals()), 30000);
    return () => clearInterval(interval);
  }, [dispatch]);

  // Animation effect for Team Light
  useEffect(() => {
    const formattedScore = totalLightScore.toString().padStart(4, '0');
    if (previousLightScore !== formattedScore) {
      const changedDigits: number[] = [];
      for (let i = 0; i < formattedScore.length; i++) {
        if (previousLightScore[i] !== formattedScore[i]) {
          changedDigits.push(i);
        }
      }
      setAnimatingLightDigits(changedDigits);
      const timer = setTimeout(() => {
        setAnimatingLightDigits([]);
        setPreviousLightScore(formattedScore);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [totalLightScore, previousLightScore]);

  // Animation effect for Team Dark
  useEffect(() => {
    const formattedScore = totalDarkScore.toString().padStart(4, '0');
    if (previousDarkScore !== formattedScore) {
      const changedDigits: number[] = [];
      for (let i = 0; i < formattedScore.length; i++) {
        if (previousDarkScore[i] !== formattedScore[i]) {
          changedDigits.push(i);
        }
      }
      setAnimatingDarkDigits(changedDigits);
      const timer = setTimeout(() => {
        setAnimatingDarkDigits([]);
        setPreviousDarkScore(formattedScore);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [totalDarkScore, previousDarkScore]);

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.content}>
        <Typography className={classes.title}>
          Gustul Kozel răcorește,
          <br />
          inspiră și provoacă la battle!
        </Typography>

        <Typography className={classes.subtitle}>
          Taberele sunt create – sunt cei care susțin că berea <strong>Kozel Blonde</strong> merită statutul de „gustul cel mai ușoooor de băut” <strong>#TeamBlonde</strong>
        </Typography>

        <Typography className={classes.subtitle}>
          În schimb, a doua tabără susține că berea Kozel Dark ocupă locul I pe podiumul Kozel <strong>#TeamDark</strong>
        </Typography>
        <Typography className={classes.subtitle}>
          E rândul tău acum – <strong>Tu pe care o preferi?</strong>
        </Typography>
        <Typography className={classes.subtitle}>
          Alege #TeamBlonde sau #TeamDark și susține-ți echipa aducând cât mai multe puncte.
          Dă start jocului de dexteritate de mai jos, strânge puncte și înscrie-te la tombolă. <strong>Poți câștiga unul din cele 10 hanorace țapene KOZEL!</strong>
        </Typography>
        <Typography className={classes.subtitle}>
          Ce-i mai bun din ambele lumi: și ușoooor de băut, și gust surprinzător de proaspăt!
          </Typography>

        <img
          src={realTimeScoreImg}
          alt="Real Time Score"
          className={classes.realTimeScoreImage}
        />

        <Box className={classes.scoreSection}>
          <Box className={classes.scoresRow}>
            <Box className={classes.teamLightContainer}>
              <ScoreDisplay
                score={totalLightScore}
                previousScore={previousLightScore}
                animatingDigits={animatingLightDigits}
              />
              <img
                src={likesWhite}
                alt="Team BLONDE Likes"
                className={classes.likeIcon}
              />
            </Box>

            <Box className={classes.teamDarkContainer}>
              <img
                src={likesDark}
                alt="Team Dark Likes"
                className={classes.likeIcon}
              />
              <ScoreDisplay
                score={totalDarkScore}
                previousScore={previousDarkScore}
                animatingDigits={animatingDarkDigits}
              />
            </Box>
          </Box>

          <img
            src={lightVsDark}
            alt="Light VS Dark"
            className={classes.lightVsDarkImage}
          />
          
        </Box>
      </Box>
    </Box>
  );
};

export default RealTimeScore;